import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Riquadro } from '../componenti/base.tsx'
import { FotoDocumento } from '../componenti/FotoDocumento.tsx'
import { CompilaDalDocumento } from '../componenti/CompilaDalDocumento.tsx'
import { LATO_LUNGO_MASSIMO } from '../servizi/regole-foto.ts'
import { DatoDerivato, useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import {
  CampiAbitativa, CampiCondizione, CampiConoscenza, CampiDatiAnagrafici, CampiFormazione, CampoSede,
  CampoTipoPersona,
} from './SchedaSezioni.tsx'
import {
  NOMI_MESI, campoDiErrore, corpoAbitativa, corpoAnagrafica, corpoCondizione, corpoFormazione, corpoServizi,
  corpoVuoto, nessunErrore, oggiLocale, permessoSullaSede, statiNuovi, tipoOperatore, verificaAbitativa, verificaAnagrafica,
  verificaCondizione, verificaFormazione, verificaServizi,
  type CataloghiRegole, type Errori, type SezioneNonRegistrata, type StatiScheda,
} from '../servizi/regole-anagrafica.ts'
import {
  corpoSottoscrizione, sottoscrizioneIniziata, sottoscrizioneNuova, verificaSottoscrizione,
} from '../servizi/regole-consensi.ts'
import { CampiSottoscrizione, useModuloConsensi } from './SchedaConsensi.tsx'

type Doppione = { id: string; numero_scheda: number; nome?: string; cognome?: string; data_nascita?: string | null }
type Doppioni = { certi: Doppione[]; probabili: Doppione[]; possibili: Doppione[]; fuori_perimetro?: number }

type ErroriModulo = Record<keyof StatiScheda | 'consensi', Errori>
const senzaErrori = (): ErroriModulo => ({
  anagrafica: {}, condizione: {}, formazione: {}, servizi: {}, abitativa: {}, consensi: {},
})

const CAMPI_CONDIZIONE = ['condizione_giuridica', 'motivo_permesso', 'permesso_scadenza']

/**
 * Nuova scheda utente, nell'ordine del modulo M03/I/SER/01 Rev 05: intestazione con il tipo di
 * persona, come e' venuto a conoscenza del Servizio, dati anagrafici, condizione giuridica,
 * formazione – lavoro, condizione abitativa e familiare. Per chi rappresenta un ente le ultime tre
 * sezioni non si mostrano, non si verificano e non si inviano (D-07).
 *
 * Prima di creare si controllano i doppioni: il codice fiscale e' decisivo, altrimenti si
 * confrontano nome, cognome e data di nascita. La scheda nasce con POST /persone; formazione –
 * lavoro, servizi bancari e condizione abitativa hanno rotte proprie e si registrano subito dopo.
 * Se una di queste non riesce la scheda resta creata e la pagina della scheda dice quale sezione
 * manca, invece di lasciare un modulo che al secondo invio creerebbe un doppione.
 */
export function NuovaPersona() {
  const vaiA = useNavigate()
  const { io, cataloghi } = useSessione()
  const cat = (cataloghi ?? {}) as CataloghiRegole
  const incarichi = io?.incarichi ?? []

  // Senza un incarico di organizzazione la sede decide il perimetro della creazione: POST /persone
  // senza sede risponderebbe «non trovato», quindi il campo diventa obbligatorio.
  const incarichiCreazione = incarichi.filter((i) => i.permessi.includes('persona.crea'))
  const sedeObbligatoria = !incarichiCreazione.some((i) => i.ambito.tipo === 'organizzazione')
  const sediIncarico = [...new Set(incarichiCreazione
    .filter((i) => i.ambito.tipo === 'sede' && i.ambito.id).map((i) => i.ambito.id as string))]

  const [stati, setStati] = useState(() => statiNuovi(sediIncarico.length === 1 ? sediIncarico[0] : ''))
  const [errori, setErrori] = useState(senzaErrori)
  const [errore, setErrore] = useState<string | null>(null)
  const [doppioni, setDoppioni] = useState<Doppioni | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  // Scheda gia' salvata, in attesa del passo della foto del documento (D-03), con gli avvisi delle
  // sezioni non registrate da passare alla scheda.
  const [salvata, setSalvata] = useState<{ id: string; numero: number; avvisi: SezioneNonRegistrata[] } | null>(null)
  const [fotoCaricate, setFotoCaricate] = useState(0)
  // «Compila dal documento» (lettura con l'AI): i campi riempiti e non ancora toccati, da evidenziare,
  // e il file letto, da proporre come documento d'identita' dopo il salvataggio.
  const [letti, setLetti] = useState<ReadonlySet<string>>(new Set())
  const [documentoLetto, setDocumentoLetto] = useState<{ file: File; tipo: string | null } | null>(null)
  const modulo = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(modulo, tentativo)

  const oggi = oggiLocale()
  // Consensi (D-09): facoltativi alla creazione, si compilano se il modulo e' gia' firmato. Si
  // registrano solo con consenso.registra sulla sede della scheda, come la rotta li verifica.
  const [consensi, setConsensi] = useState(() => sottoscrizioneNuova(oggi))
  const { modulo: moduloConsensi, errore: erroreModuloConsensi } = useModuloConsensi(incarichi.some((i) => i.permessi.includes('consenso.registra')))
  const registraConsensi = !!moduloConsensi
    && permessoSullaSede(incarichi, 'consenso.registra', stati.anagrafica.sede_riferimento)
  // 481: con i dati anonimizzati la persona non firma l'informativa: i consensi non si raccolgono.
  const anonima = stati.anagrafica.dati_anonimizzati
  const conConsensi = registraConsensi && !anonima && sottoscrizioneIniziata(consensi)
  const operatore = tipoOperatore(stati.anagrafica.tipo)
  // La condizione giuridica e' un dato particolare: POST /persone la accetta solo con
  // persona.modifica_particolari sulla sede scelta. Senza, la sezione non si compila qui.
  const scriveParticolariSu = (sede: string) => permessoSullaSede(incarichi, 'persona.modifica_particolari', sede)
  const scriveParticolari = scriveParticolariSu(stati.anagrafica.sede_riferimento)

  /** Un campo riempito dal documento e poi toccato dall'operatore non e' piu' da evidenziare. */
  const tocca = (campi: string[]) => setLetti((l) => {
    if (!campi.some((c) => l.has(c))) return l
    const restano = new Set(l)
    campi.forEach((c) => restano.delete(c))
    return restano
  })

  const cambia = <K extends keyof StatiScheda>(parte: K) => (parziale: Partial<StatiScheda[K]>) => {
    tocca(Object.keys(parziale))
    setStati((s) => ({ ...s, [parte]: { ...s[parte], ...parziale } }))
  }

  /** Dati anagrafici; attivando «Dati anonimizzati» (481) si svuota anche la scadenza del titolo di soggiorno. */
  const cambiaAnagrafica = (parziale: Partial<StatiScheda['anagrafica']>) => {
    tocca([...Object.keys(parziale), ...(parziale.dati_anonimizzati ? ['permesso_scadenza'] : [])])
    setStati((s) => ({
      ...s,
      anagrafica: { ...s.anagrafica, ...parziale },
      ...(parziale.dati_anonimizzati ? { condizione: { ...s.condizione, permesso_scadenza: '' } } : {}),
    }))
  }

  /** Cambiando sede la condizione giuridica gia' compilata si svuota se sulla nuova sede non si scrive. */
  const cambiaSede = (sede: string) => {
    if (!scriveParticolariSu(sede)) tocca(CAMPI_CONDIZIONE)
    setStati((s) => ({
      ...s,
      anagrafica: { ...s.anagrafica, sede_riferimento: sede },
      ...(scriveParticolariSu(sede) ? {} : { condizione: statiNuovi().condizione }),
    }))
  }

  const segnala = (nuovi: ErroriModulo, messaggio = 'Correggere i campi segnalati.') => {
    setErrori(nuovi); setErrore(messaggio); setTentativo((t) => t + 1)
  }

  async function salva(confermaDoppione: boolean) {
    setErrore(null); setDoppioni(null)
    const verificati: ErroriModulo = {
      anagrafica: verificaAnagrafica(stati.anagrafica, cat, { sedeObbligatoria, oggi }),
      condizione: !operatore && scriveParticolari ? verificaCondizione(stati.condizione, cat) : {},
      formazione: operatore ? {} : verificaFormazione(stati.formazione, cat),
      servizi: operatore ? {} : verificaServizi(stati.servizi, cat),
      abitativa: operatore ? {} : verificaAbitativa(stati.abitativa, cat),
      consensi: conConsensi && moduloConsensi ? verificaSottoscrizione(consensi, moduloConsensi, oggi) : {},
    }
    if (!Object.values(verificati).every(nessunErrore)) { segnala(verificati); return }
    setErrori(senzaErrori())

    setInCorso(true)
    let creata: { id: string; numero_scheda: number } | null = null
    try {
      creata = await api.post<{ id: string; numero_scheda: number }>('/persone', {
        ...corpoAnagrafica(stati.anagrafica, cat),
        ...(!operatore && scriveParticolari ? corpoCondizione(stati.condizione) : {}),
        conferma_doppione: confermaDoppione,
      })
    } catch (e) {
      setInCorso(false)
      if (e instanceof ErroreApi && e.codice === 'DOPPIONE') { setDoppioni(e.corpo as Doppioni); return }
      if (e instanceof ErroreApi && e.stato === 404) {
        segnala({ ...senzaErrori(), anagrafica: { sede_riferimento: 'La sede non rientra negli incarichi in corso.' } },
          'Con gli incarichi in corso la scheda non si crea su questa sede.')
        return
      }
      const campo = e instanceof ErroreApi ? campoDiErrore(e) : null
      if (campo) {
        const parte = CAMPI_CONDIZIONE.includes(campo.campo) ? 'condizione' : 'anagrafica'
        segnala({ ...senzaErrori(), [parte]: { [campo.campo]: campo.messaggio } })
      } else {
        setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      }
      return
    }

    const nonSalvate: SezioneNonRegistrata[] = []
    const registra = async (sezione: SezioneNonRegistrata['sezione'], nome: string, chiamata: () => Promise<unknown>) => {
      try { await chiamata() } catch (e) {
        nonSalvate.push({ sezione, testo: `${nome}: ${e instanceof ErroreApi ? e.message : 'operazione non riuscita'}` })
      }
    }
    if (!operatore) {
      const lavoro = corpoFormazione(stati.formazione)
      if (!corpoVuoto(lavoro)) {
        await registra('formazione', 'Formazione – Lavoro',
          () => api.post(`/persone/${creata.id}/situazioni/lavorativa`, lavoro))
      }
      const servizi = corpoServizi(stati.servizi, cat)
      if (!corpoVuoto(servizi)) {
        await registra('servizi', 'Servizi bancari e finanziari',
          () => api.put(`/persone/${creata.id}/servizi-bancari`, servizi))
      }
      const casa = corpoAbitativa(stati.abitativa)
      if (!corpoVuoto(casa)) {
        await registra('abitativa', 'Condizione abitativa e familiare',
          () => api.post(`/persone/${creata.id}/situazioni/abitativa`, casa))
      }
    }
    // I consensi valgono anche per chi rappresenta un ente: il modulo e' lo stesso.
    if (conConsensi && moduloConsensi) {
      await registra('consensi', 'Consensi',
        () => api.post(`/persone/${creata.id}/consensi`, corpoSottoscrizione(consensi, moduloConsensi)))
    }
    setInCorso(false)
    // D-03: dopo il salvataggio e prima di uscire, la foto del documento, saltabile. Si propone a chi
    // carica documenti sulla sede della scheda. Con un incarico di ente, area o progetto la scheda
    // appena creata, ancora senza percorsi, non rientra nel perimetro, e la prenotazione del
    // documento risponderebbe «non trovato»: in quel caso si apre subito la scheda. Con i dati
    // anonimizzati (481) il documento d'identita' non si carica: si apre subito la scheda.
    if (!anonima && permessoSullaSede(incarichi, 'documento.carica', stati.anagrafica.sede_riferimento)) {
      setSalvata({ id: creata.id, numero: creata.numero_scheda, avvisi: nonSalvate })
      return
    }
    vaiA(`/persone/${creata.id}`, { state: { avvisi: nonSalvate } })
  }

  const simili = doppioni ? [...doppioni.certi, ...doppioni.probabili] : []

  if (salvata) {
    const apriScheda = () => vaiA(`/persone/${salvata.id}`, { state: { avvisi: salvata.avvisi } })
    return (
      <>
        <Intestazione titolo="Nuova scheda utente"
                      sottotitolo="Ultimo passo, facoltativo: la foto del documento." />

        {/* Regione di stato: il fuoco va subito su «Fotografa il documento», e senza l'annuncio chi usa
            un lettore di schermo sentirebbe solo il nome del comando, non l'esito del salvataggio. */}
        <div role="status">
          <Avviso tipo="successo">Scheda n. {salvata.numero} salvata.</Avviso>
        </div>

        {salvata.avvisi.length > 0 && (
          <Avviso tipo="attenzione">
            Alcune sezioni non sono state registrate: l’elenco compare aprendo la scheda.
          </Avviso>
        )}

        <Riquadro titolo="Foto del documento">
          {documentoLetto && (
            <p className="persona-nota-sezione">
              Il documento letto con «Compila dal documento» è pronto qui sotto: controlla il tipo e caricalo tra i
              documenti della scheda come documento d’identità, oppure salta il passo.
            </p>
          )}
          <p className="persona-nota-sezione">
            Dal telefono o dal tablet si apre la fotocamera; dal computer si sceglie un’immagine. La foto
            si riduce a {LATO_LUNGO_MASSIMO} px sul lato lungo, in JPEG, senza la posizione dello scatto, e si
            carica cifrata tra i documenti della scheda. Il passo si può saltare: la foto si aggiunge anche
            dopo, dalla linguetta Documenti.
          </p>
          <FotoDocumento personaId={salvata.id} fuocoIniziale caricata={() => setFotoCaricate((n) => n + 1)}
                         fileIniziale={documentoLetto?.file ?? null} tipoIniziale={documentoLetto?.tipo ?? null} />
          <div className="persona-comandi">
            <Bottone variante={fotoCaricate > 0 ? 'primario' : 'secondario'} onClick={apriScheda}>
              {fotoCaricate > 0 ? 'Apri la scheda' : 'Salta e apri la scheda'}
            </Bottone>
          </div>
        </Riquadro>
      </>
    )
  }

  return (
    <>
      <Intestazione titolo="Nuova scheda utente"
                    sottotitolo="Modulo M03/I/SER/01 Rev 05. Prima del salvataggio si controllano i possibili doppioni." />

      {/* In cima, fuori dal modulo: i suoi comandi non devono inviare la scheda. */}
      <CompilaDalDocumento stati={stati} anonima={anonima} conCondizione={!operatore && scriveParticolari}
                           usa={(esito, documento) => {
                             setStati(esito.stati)
                             setLetti((l) => new Set([...l, ...esito.letti]))
                             setDocumentoLetto(documento)
                           }} />

      <form ref={modulo} noValidate onSubmit={(e) => { e.preventDefault(); void salva(false) }}>
        <p className="persona-nota-sezione">I campi contrassegnati con * sono obbligatori.</p>

        <Riquadro titolo="Intestazione">
          <div className="griglia-modulo">
            <CampoTipoPersona valore={stati.anagrafica.tipo} errore={errori.anagrafica.tipo}
                              cambia={(tipo) => cambia('anagrafica')({ tipo })} />
            <CampoSede valore={stati.anagrafica.sede_riferimento} obbligatorio={sedeObbligatoria}
                       errore={errori.anagrafica.sede_riferimento} cambia={cambiaSede} />
            <DatoDerivato etichetta="Progetto" valore={null}
                          aiuto="Si ricava dai percorsi: si indica all’apertura di un percorso." />
            <DatoDerivato etichetta="Anno" valore={oggi.slice(0, 4)} />
            <DatoDerivato etichetta="Mese" valore={NOMI_MESI[Number(oggi.slice(5, 7)) - 1]}
                          aiuto="Anno e mese di creazione della scheda." />
            <DatoDerivato etichetta="Scheda SUS n." valore="assegnato al salvataggio" />
          </div>
        </Riquadro>

        <Riquadro titolo="Come è venuto a conoscenza del Servizio">
          <CampiConoscenza stato={stati.anagrafica} cambia={cambia('anagrafica')} errori={errori.anagrafica} />
        </Riquadro>

        <Riquadro titolo="Dati anagrafici">
          <CampiDatiAnagrafici stato={stati.anagrafica} cambia={cambiaAnagrafica} errori={errori.anagrafica}
                               letti={letti} />
        </Riquadro>

        {operatore ? (
          <Avviso tipo="informazione">
            Per chi rappresenta un ente o fa parte del personale CIDIS non si compilano condizione giuridica,
            formazione – lavoro e condizione abitativa e familiare.
          </Avviso>
        ) : (
          <>
            <Riquadro titolo="Condizione giuridica">
              {scriveParticolari
                ? <CampiCondizione stato={stati.condizione} cambia={cambia('condizione')} errori={errori.condizione}
                                   anonimizzati={anonima} letti={letti} />
                : (
                  <p className="tenue piccolo">
                    Con gli incarichi in corso sulla sede scelta la condizione giuridica non si compila qui: la
                    registra sulla scheda chi ha il permesso di modificare i dati particolari.
                  </p>
                )}
            </Riquadro>

            <Riquadro titolo="Formazione – Lavoro">
              <CampiFormazione stato={stati.formazione} cambia={cambia('formazione')} errori={errori.formazione}
                               servizi={stati.servizi} cambiaServizi={cambia('servizi')}
                               erroriServizi={errori.servizi} />
            </Riquadro>

            <Riquadro titolo="Condizione abitativa e familiare">
              <CampiAbitativa stato={stati.abitativa} cambia={cambia('abitativa')} errori={errori.abitativa} />
            </Riquadro>
          </>
        )}

        <Riquadro titolo="Presa visione dell’informativa e consensi">
          {anonima
            ? (
              <p className="persona-nota-sezione">
                Dati anonimizzati: la persona non firma l’informativa, i consensi non si raccolgono e la scheda non si
                stampa per la firma. Gli interventi si registrano senza i consensi.
              </p>
            )
            : !incarichi.some((i) => i.permessi.includes('consenso.registra'))
            ? <p className="tenue piccolo">Con gli incarichi in corso i consensi non si registrano.</p>
            // Senza i testi del modulo (nessuna revisione in uso, errore di rete) la sezione non si compila:
            // lo si dice, invece di lasciare il messaggio di caricamento.
            : erroreModuloConsensi
              ? (
                <Avviso tipo="errore">
                  {erroreModuloConsensi} Alla creazione i consensi non si registrano: si registrano dalla scheda
                  quando i testi del modulo sono disponibili.
                </Avviso>
              )
              : !moduloConsensi
                ? <p className="tenue piccolo">Caricamento dei testi del modulo…</p>
                : !registraConsensi
                  ? (
                    <p className="tenue piccolo">
                      Con gli incarichi in corso sulla sede scelta i consensi si registrano dalla scheda, da chi ha il
                      permesso.
                    </p>
                  )
                  : (
                    <>
                      <p className="persona-nota-sezione">
                        Facoltativa alla creazione: si compila se la persona ha già firmato il modulo. Altrimenti la
                        scheda si stampa, si fa firmare e i consensi si registrano dalla scheda. Senza i consensi 1 e 2
                        espressi gli interventi non si registrano.
                      </p>
                      <CampiSottoscrizione modulo={moduloConsensi} stato={consensi}
                                           cambia={(parziale) => setConsensi((s) => ({ ...s, ...parziale }))}
                                           errori={errori.consensi} oggi={oggi} />
                    </>
                  )}
        </Riquadro>

        {/* Esito dell'invio accanto ai comandi: il modulo e' lungo, e in testa alla pagina l'avviso
            resterebbe fuori dalla vista di chi ha appena premuto «Salva la scheda». */}
        {errore && <Avviso tipo="errore">{errore}</Avviso>}

        {doppioni && (
          <div role="alert">
            <Avviso tipo="attenzione">
              <strong>Esiste già una scheda molto simile.</strong>
              <ul className="elenco-avviso">
                {simili.map((d) => (
                  <li key={d.id}>
                    <Link to={`/persone/${d.id}`}>
                      scheda n. {d.numero_scheda} — {d.cognome ?? ''} {d.nome ?? ''} {d.data_nascita ?? ''}
                    </Link>
                  </li>
                ))}
              </ul>
              {!!doppioni.fuori_perimetro && (
                <p>Altre schede simili non rientrano negli incarichi in corso.</p>
              )}
              {/* «Unisci schede» (473): se la persona ha gia' due schede, le unisce la Direzione. */}
              <p className="piccolo">
                Se è la stessa persona, apri la sua scheda invece di crearne un’altra. Se ne esistono già due, la
                Direzione può unirle con «Unisci con un’altra scheda» dalla scheda da tenere.
              </p>
              <div className="riga">
                <Bottone variante="secondario" onClick={() => setDoppioni(null)}>Torna al modulo</Bottone>
                <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva(true)}>
                  Salva come persona distinta
                </Bottone>
              </div>
            </Avviso>
          </div>
        )}

        <div className="persona-comandi">
          <Link className="bottone bottone-secondario" to="/persone">Annulla</Link>
          <Bottone tipo="submit" variante="primario" inCorso={inCorso}>Salva la scheda</Bottone>
        </div>
      </form>
    </>
  )
}
